import { Auth0Provider as Auth0ProviderSDK } from '@auth0/auth0-react';
import { useGetConfigAuth } from 'src/hooks/api/useConfigEndpoint';
import SuspenseLoader from '../SuspenseLoader';
import { AuthProvider } from 'src/contexts/AuthContext/AuthContext';
import { clientConfig } from 'src/api/axios/config';
import { useEffect } from 'react';

interface Auth0ProviderProps {
  children: React.ReactNode;
}

const VITE_API_PATH = import.meta.env.VITE_API_PATH ?? 'access.wicketsoft.com';
// Capacitor callback URL scheme - doesn't need to be tenant-specific since Capacitor routes
// the deep link back to the webview, and Auth0 handles tenant logic server-side
export const CAPACITOR_CALLBACK = 'com.wicketsoft.admin://capacitor/com.wicketsoft.admin/callback';

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
}

declare global {
  interface Window {
    Capacitor?: CapacitorGlobal;
  }
}

export function isCapacitorNative(): boolean {
  return Boolean(window.Capacitor?.isNativePlatform?.());
}

export function getRedirectUri(): string {
  return isCapacitorNative() ? CAPACITOR_CALLBACK : window.location.origin;
}

const Auth0Provider = ({ children }: Auth0ProviderProps) => {
  const { data: configAuthData, isLoading, isError } = useGetConfigAuth();

  const domain = configAuthData?.domain || '';
  const clientId = configAuthData?.clientId || '';
  const client = configAuthData?.client || 'demo';
  const domainSlug = window.location.hostname.split('.')[0] || 'demo';
  const auth0ApiAudienceUrl = `https://${domainSlug}.${VITE_API_PATH}/api`;

  useEffect(() => {
    // domain check required or will always default to demo client on non-auth0 clients
    if (domain) clientConfig.setClient(client);
  }, [domain, client]);

  // Docs for working with auth0 locally:
  // https://wicketsoft.atlassian.net/wiki/spaces/wa/pages/2902458370/Authentication+with+Auth0
  const authorizationParams = {
    redirect_uri: getRedirectUri(),
    audience: auth0ApiAudienceUrl,
  };

  if (isLoading) {
    return <SuspenseLoader />;
  }

  if (isError) {
    // I think we should render <Auth0ProviderSDK> but handle it somehow differently , any suggestions what to do in case of error ?
    return <div>Error loading Auth0</div>;
  }

  return (
    <Auth0ProviderSDK
      domain={domain}
      clientId={clientId}
      authorizationParams={authorizationParams}
      cacheLocation="localstorage"
    >
      <AuthProvider configAuthData={configAuthData}>{children}</AuthProvider>
    </Auth0ProviderSDK>
  );
};

export default Auth0Provider;
